/** Per-character timings for one clip, in seconds relative to the clip's start. */
export interface Alignment {
  characters: string[];
  starts: number[];
  ends: number[];
}

export interface TimedLine {
  text: string;
  /** Where the clip starts on the video clock, in seconds. */
  startAt: number;
  alignment?: Alignment;
  duration: number;
}

export interface CueWord {
  start: number;
  end: number;
  /** The word with the spaces and marks that follow it: a cue's words joined are its text. */
  text: string;
}

export interface Cue {
  start: number;
  end: number;
  text: string;
  /** When each word is spoken, for highlight-as-spoken. Absent when they cannot be told apart. */
  words?: CueWord[];
}

const DEFAULT_MAX_CUE_SECONDS = 7;
const MIN_CUE_SECONDS = 0.3;

/** Marks that always end a sentence. Thai has none, so a Thai line stays one sentence. */
const HARD_ENDS = new Set(["。", "？", "！", "؟", "।"]);
/** Marks that end a sentence only when followed by a space, a closer or the end of the line. */
const SOFT_ENDS = new Set([".", "?", "!"]);
const CLOSERS = new Set(['"', "'", "”", "’", ")", "]", "」", "』", "»"]);

interface Unit {
  ch: string;
  start: number;
  end: number;
}

const isSpace = (ch: string): boolean => /^\s+$/u.test(ch);
const isEnd = (ch: string): boolean => HARD_ENDS.has(ch) || SOFT_ENDS.has(ch);

/** Units on the video clock, from the alignment when it matches, else spread evenly over the clip. */
const finite = (n: number): number => (Number.isFinite(n) ? n : 0);

function unitsOf(raw: TimedLine): Unit[] {
  const line = { ...raw, startAt: finite(raw.startAt), duration: finite(raw.duration) };
  const a = line.alignment;
  if (
    a &&
    a.characters.length > 0 &&
    a.starts.length === a.characters.length &&
    a.ends.length === a.characters.length
  ) {
    return a.characters.map((ch, i) => ({
      ch,
      start: line.startAt + finite(a.starts[i]),
      end: line.startAt + finite(a.ends[i]),
    }));
  }
  const chars = [...line.text];
  const step = line.duration / Math.max(chars.length, 1);
  return chars.map((ch, i) => ({ ch, start: line.startAt + i * step, end: line.startAt + (i + 1) * step }));
}

function sentences(units: Unit[]): Unit[][] {
  const out: Unit[][] = [];
  let current: Unit[] = [];
  for (let i = 0; i < units.length; i++) {
    current.push(units[i]);
    if (!isEnd(units[i].ch)) continue;
    const next = units[i + 1]?.ch;
    if (SOFT_ENDS.has(units[i].ch) && next !== undefined && !isSpace(next) && !CLOSERS.has(next) && !isEnd(next)) {
      continue; // "3.5", "e.g." and the like
    }
    // Keep repeated marks ("?!") and closing quotes with the sentence they close.
    while (units[i + 1] && (isEnd(units[i + 1].ch) || CLOSERS.has(units[i + 1].ch))) current.push(units[++i]);
    out.push(current);
    current = [];
  }
  if (current.length) out.push(current);
  return out;
}

function trimmed(units: Unit[]): Unit[] {
  let from = 0;
  let to = units.length;
  while (from < to && isSpace(units[from].ch)) from++;
  while (to > from && isSpace(units[to - 1].ch)) to--;
  return units.slice(from, to);
}

const spanOf = (units: Unit[]): number => units[units.length - 1].end - units[0].start;

/** Whether a cue may start at unit i without separating a combining mark from its base. */
function graphemeStarts(units: Unit[]): (i: number) => boolean {
  const Segmenter = (Intl as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (!Segmenter) return () => true;
  const offsets: number[] = [];
  let text = "";
  for (const u of units) {
    offsets.push(text.length);
    text += u.ch;
  }
  const starts = new Set<number>();
  for (const seg of new Segmenter(undefined, { granularity: "grapheme" }).segment(text)) starts.add(seg.index);
  return (i) => starts.has(offsets[i]);
}

/** Split a run longer than `max` seconds near its even divisions, at spaces when it has them. */
function split(units: Unit[], max: number): Unit[][] {
  const run = trimmed(units);
  if (run.length === 0) return [];
  if (spanOf(run) <= max || run.length < 2) return [run];
  const target = run[0].start + spanOf(run) / Math.ceil(spanOf(run) / max);
  // A boundary sits before index i, so both sides are non-empty for 1 <= i <= length - 1.
  const distance = (i: number): number => Math.abs(run[i].start - target);
  let best = -1;
  for (let i = 1; i < run.length - 1; i++) {
    if (isSpace(run[i].ch) && (best < 0 || distance(i) < distance(best))) best = i;
  }
  if (best < 0) {
    const ok = graphemeStarts(run);
    best = -1;
    for (let i = 1; i < run.length; i++) {
      if (ok(i) && (best < 0 || distance(i) < distance(best))) best = i;
    }
    if (best < 0) return [run]; // one unbreakable grapheme cluster
  }
  return [...split(run.slice(0, best), max), ...split(run.slice(best), max)];
}

/** A mark that opens what follows it, so it goes with the next word rather than the last. */
const OPENING = /^[\p{Ps}\p{Pi}¿¡]/u;

let segmenterMade = false;
let segmenterCache: Intl.Segmenter | null = null;

/** One word segmenter for every cue; null where the platform has none. */
function wordSegmenter(): Intl.Segmenter | null {
  if (!segmenterMade) {
    const Segmenter = (Intl as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
    segmenterCache = Segmenter ? new Segmenter(undefined, { granularity: "word" }) : null;
    segmenterMade = true;
  }
  return segmenterCache;
}

/**
 * A run's words on the video clock, covering its text exactly — `text` is the
 * run as the cue shows it. Words are found by the platform's word segmenter, so
 * Japanese and Thai, which do not space their words, are split too; spaces and
 * closing marks ride with the word before them, an opening mark with the word after.
 * Null when the run's text is not what the cue shows (a "-->" rewritten), so a
 * highlight never drifts from the words on screen.
 */
function wordsOf(run: Unit[], text: string): CueWord[] | null {
  let shown = "";
  const unitAt: Unit[] = [];
  for (const unit of trimmed(run)) {
    const ch = isSpace(unit.ch) ? " " : unit.ch;
    if (ch === " " && shown.endsWith(" ")) continue;
    for (let i = 0; i < ch.length; i++) unitAt.push(unit);
    shown += ch;
  }
  if (shown !== text) return null;

  const segmenter = wordSegmenter();
  const segments = segmenter
    ? [...segmenter.segment(text)].map((s) => ({ index: s.index, text: s.segment, word: s.isWordLike === true }))
    : [...text.matchAll(/\S+|\s+/gu)].map((m) => ({ index: m.index ?? 0, text: m[0], word: /\S/u.test(m[0]) }));

  const words: CueWord[] = [];
  let current: { text: string; from: number; to: number } | null = null;
  // An opening mark (« ¿ ( “) and anything after it, waiting for the word it opens.
  let lead = "";
  for (const segment of segments) {
    if (segment.word) {
      // A word joined to the one before without a space (Japanese, Thai, "e-mail") is its own.
      if (current) words.push({ start: unitAt[current.from].start, end: unitAt[current.to].end, text: current.text });
      current = { text: lead + segment.text, from: segment.index, to: segment.index + segment.text.length - 1 };
      lead = "";
    } else if (current && !lead && !OPENING.test(segment.text)) {
      // Spaces and closing marks ride with the word before them.
      current.text += segment.text;
    } else {
      lead += segment.text;
    }
  }
  if (current)
    words.push({ start: unitAt[current.from].start, end: unitAt[current.to].end, text: current.text + lead });
  else if (lead && words.length > 0) words[words.length - 1].text += lead;
  if (words.length === 0 || words.map((w) => w.text).join("") !== text) return null;
  // Lit until the next one starts, as the English is: no dark gap at each space.
  for (let i = 0; i < words.length - 1; i++) words[i].end = Math.max(words[i].end, words[i + 1].start);
  return words;
}

export function cuesFromLines(lines: TimedLine[], maxCueSeconds: number = DEFAULT_MAX_CUE_SECONDS): Cue[] {
  const max = maxCueSeconds > 0 ? maxCueSeconds : DEFAULT_MAX_CUE_SECONDS;
  const cues: Cue[] = [];
  for (const line of lines) {
    for (const sentence of sentences(unitsOf(line))) {
      for (const run of split(sentence, max)) {
        const text = run
          .map((u) => u.ch)
          .join("")
          .replace(/\s+/gu, " ")
          .replace(/-->/g, "->")
          .trim();
        if (!text) continue;
        const words = wordsOf(run, text);
        cues.push({ start: run[0].start, end: run[run.length - 1].end, text, ...(words ? { words } : {}) });
      }
    }
  }
  cues.sort((a, b) => a.start - b.start);
  const out: Cue[] = [];
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i];
    const next = cues[i + 1];
    if (cue.end - cue.start < MIN_CUE_SECONDS) cue.end = cue.start + MIN_CUE_SECONDS;
    if (next && cue.end > next.start) cue.end = next.start;
    if (cue.end > cue.start) {
      out.push(cue);
    } else if (next) {
      next.text = `${cue.text} ${next.text}`; // never lose words: fold into the cue that takes over
      delete next.words; // the folded words have no time of their own left
    } else if (out.length) {
      out[out.length - 1].text += ` ${cue.text}`;
      delete out[out.length - 1].words;
    } else {
      out.push({ ...cue, end: cue.start + MIN_CUE_SECONDS });
    }
  }
  return out;
}

function timestamp(seconds: number, separator: "." | ","): string {
  const total = Math.max(0, Math.round(finite(seconds) * 1000));
  const pad = (n: number, width: number): string => String(n).padStart(width, "0");
  const h = Math.floor(total / 3_600_000);
  const m = Math.floor((total % 3_600_000) / 60_000);
  const s = Math.floor((total % 60_000) / 1000);
  return `${pad(h, 2)}:${pad(m, 2)}:${pad(s, 2)}${separator}${pad(total % 1000, 3)}`;
}

export function toWebVtt(cues: Cue[]): string {
  const escape = (t: string): string => t.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const blocks = cues.map((c) => `${timestamp(c.start, ".")} --> ${timestamp(c.end, ".")}\n${escape(c.text)}\n`);
  return `WEBVTT\n\n${blocks.join("\n")}`.replace(/\n\n$/, "\n");
}

export function toSrt(cues: Cue[]): string {
  return cues
    .map((c, i) => `${i + 1}\n${timestamp(c.start, ",")} --> ${timestamp(c.end, ",")}\n${c.text}\n`)
    .join("\n");
}
